// 环境变量管理和验证

interface EnvConfig {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_DEV_MODE?: string;
}

// 获取并验证环境变量
export const getEnvConfig = (): EnvConfig => {
  const config: EnvConfig = {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    VITE_DEV_MODE: import.meta.env.VITE_DEV_MODE,
  };

  return config;
};

// 验证必需的环境变量
export const validateEnvConfig = (
  config: EnvConfig
): { isValid: boolean; missingVars: string[] } => {
  const missingVars: string[] = [];

  // 在生产环境中，Supabase配置是必需的
  if (import.meta.env.PROD) {
    if (!config.VITE_SUPABASE_URL) {
      missingVars.push('VITE_SUPABASE_URL');
    }
    if (!config.VITE_SUPABASE_ANON_KEY) {
      missingVars.push('VITE_SUPABASE_ANON_KEY');
    }
  }

  // 验证Supabase URL格式
  if (config.VITE_SUPABASE_URL) {
    try {
      const url = new URL(config.VITE_SUPABASE_URL);
      if (!url.hostname.includes('supabase')) {
        console.warn('⚠️ VITE_SUPABASE_URL does not appear to be a valid Supabase URL');
      }
    } catch {
      console.error('❌ VITE_SUPABASE_URL is not a valid URL format');
      missingVars.push('VITE_SUPABASE_URL (invalid format)');
    }
  }

  return {
    isValid: missingVars.length === 0,
    missingVars,
  };
};

// 检查是否为开发模式
export const isDevelopment = (): boolean => {
  return import.meta.env.DEV || getEnvConfig().VITE_DEV_MODE === 'true';
};

// 检查是否为生产模式
export const isProduction = (): boolean => {
  return import.meta.env.PROD && getEnvConfig().VITE_DEV_MODE !== 'true';
};

// 安全地获取环境变量（不暴露敏感信息）
export const getSafeEnvInfo = () => {
  const config = getEnvConfig();

  return {
    hasSupabaseUrl: !!config.VITE_SUPABASE_URL,
    hasSupabaseKey: !!config.VITE_SUPABASE_ANON_KEY,
    isDev: isDevelopment(),
    isProd: isProduction(),
    supabaseHost: config.VITE_SUPABASE_URL
      ? new URL(config.VITE_SUPABASE_URL).hostname
      : 'not configured',
  };
};

// 在应用启动时验证环境变量
export const initializeEnv = (): { success: boolean; message?: string } => {
  const config = getEnvConfig();
  const validation = validateEnvConfig(config);

  if (!validation.isValid) {
    const message = `缺少必需的环境变量: ${validation.missingVars.join(', ')}`;
    console.error('❌ Environment validation failed:', message);

    // 在开发模式下给出更详细的指导
    if (isDevelopment()) {
      console.log(`
🔧 开发模式检测到缺少环境变量
请创建 .env 文件并添加以下配置：

VITE_SUPABASE_URL=你的Supabase项目URL
VITE_SUPABASE_ANON_KEY=你的Supabase匿名密钥

如果你还没有Supabase项目，应用将自动切换到Mock模式。
      `);
    }

    return { success: false, message };
  }


  return { success: true };
};
